import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

const AdminUIContext = createContext(null);

let toastId = 0;

export function AdminUIProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const confirmResolver = useRef(null);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    ({ type = "info", title, message, duration = 4500 }) => {
      const id = ++toastId;
      setToasts((prev) => [...prev, { id, type, title, message }]);
      if (duration > 0) {
        window.setTimeout(() => dismissToast(id), duration);
      }
      return id;
    },
    [dismissToast],
  );

  const toast = useMemo(
    () => ({
      success: (message, title = "Success") =>
        pushToast({ type: "success", title, message }),
      error: (message, title = "Error") =>
        pushToast({ type: "error", title, message, duration: 7000 }),
      info: (message, title = "Info") =>
        pushToast({ type: "info", title, message }),
    }),
    [pushToast],
  );

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      confirmResolver.current = resolve;
      setConfirmState({
        title: options.title || "Confirm",
        message: options.message || "Are you sure?",
        confirmLabel: options.confirmLabel || "Confirm",
        cancelLabel: options.cancelLabel || "Cancel",
        danger: Boolean(options.danger),
      });
    });
  }, []);

  const resolveConfirm = useCallback((value) => {
    if (confirmResolver.current) {
      confirmResolver.current(value);
      confirmResolver.current = null;
    }
    setConfirmState(null);
  }, []);

  const value = useMemo(
    () => ({
      toast,
      confirm,
      toasts,
      dismissToast,
      confirmState,
      resolveConfirm,
    }),
    [toast, confirm, toasts, dismissToast, confirmState, resolveConfirm],
  );

  return (
    <AdminUIContext.Provider value={value}>{children}</AdminUIContext.Provider>
  );
}

export function useAdminUI() {
  const ctx = useContext(AdminUIContext);
  if (!ctx) {
    throw new Error("useAdminUI must be used within AdminUIProvider");
  }
  return ctx;
}
